import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { Alert, Snackbar } from '@mui/material';
import { createUser } from '../../redux/actions/userManageAction';

const CreateNewUser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;
  
  const userCreate = useSelector((state) => state.createUser);
  const { userLoading, userError } = userCreate;

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('inhouse_installer');

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertType, setAlertType] = useState('success'); // 'success' or 'error'
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    if (!userInfo) {
      navigate('/sign-in');
    }
    window.scrollTo(0, 0);
  }, [userInfo, navigate]);

  useEffect(() => {
    if (userError) {
      setAlertType('error');
      setAlertMessage(userError);
      setAlertOpen(true);
    }
  }, [userError]);

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(createUser({ username, email, password, roles: [role] }, userInfo.token));
    if (!userError) {
      setAlertType('success');
      setAlertMessage('User created successfully!');
      navigate('/user/manage-user');
    } else {
      setAlertType('error');
      setAlertMessage('Failed to create user.');
    }
    setAlertOpen(true);
  };

  return (
    <div className="container-fluid py-4" style={{minHeight: '100vh' }}>
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h2 className="mb-1 text-white fw-bold">Create New User</h2>
              <p className="text-light opacity-75 mb-0">Add a new user to the system with appropriate role permissions</p>
            </div>
            <div>
              <button 
                type="button" 
                className="btn btn-outline-light me-2"
                onClick={() => navigate('/user/manage-user')}
                style={{ borderColor: '#6366f1', color: '#a5b4fc' }}
              >
                <i className="fas fa-arrow-left me-2"></i>Back to Users
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="row justify-content-center">
        <div className="col-12 col-xl-10">
          <form onSubmit={handleSubmit}>
            <div className="card shadow-lg border-0" style={{ 
              backgroundColor: '#1a1f3a', 
              borderRadius: '16px',
              border: '1px solid #2d3748'
            }}>
              {/* Card Header */}
              <div className="card-header border-bottom py-4" style={{ 
                backgroundColor: '#232946', 
                borderBottom: '1px solid #2d3748',
                borderRadius: '16px 16px 0 0'
              }}>
                <div className="d-flex align-items-center">
                  <div className="rounded-circle p-3 me-3" style={{ 
                    backgroundColor: '#6366f1', 
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
                  }}>
                    <i className="fas fa-user-plus text-white"></i>
                  </div>
                  <div>
                    <h5 className="mb-0 fw-semibold text-white">User Information</h5>
                    <small className="text-light opacity-75">Fill in the details to create a new user account</small>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="card-body p-4" style={{ backgroundColor: '#1a1f3a' }}>
                <div className="row g-4">
                  {/* Username Field */}
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label fw-semibold text-white mb-2">
                        <i className="fas fa-user me-2" style={{ color: '#6366f1' }}></i>Username
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-lg border-2 text-white"
                        placeholder="Enter username"
                        required
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        style={{ 
                          borderRadius: '12px',
                          backgroundColor: '#0f1419',
                          borderColor: '#2d3748',
                          color: '#ffffff'
                        }}
                      />
                      <small className="text-light opacity-75">Username should be unique and easy to remember</small>
                    </div>
                  </div>

                  {/* Email Field */}
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label fw-semibold text-white mb-2">
                        <i className="fas fa-envelope me-2" style={{ color: '#6366f1' }}></i>Email Address
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control form-control-lg border-2 text-white"
                        placeholder="Enter email address"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{ 
                          borderRadius: '12px',
                          backgroundColor: '#0f1419',
                          borderColor: '#2d3748',
                          color: '#ffffff'
                        }}
                      />
                      <small className="text-light opacity-75">Used for login and notifications</small>
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label fw-semibold text-white mb-2">
                        <i className="fas fa-lock me-2" style={{ color: '#6366f1' }}></i>Password
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="password"
                        className="form-control form-control-lg border-2 text-white"
                        placeholder="Enter secure password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{ 
                          borderRadius: '12px',
                          backgroundColor: '#0f1419',
                          borderColor: '#2d3748',
                          color: '#ffffff'
                        }}
                      />
                      <small className="text-light opacity-75">Minimum 8 characters with letters and numbers</small>
                    </div>
                  </div>

                  {/* Role Field */}
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label fw-semibold text-white mb-2">
                        <i className="fas fa-user-tag me-2" style={{ color: '#6366f1' }}></i>User Role
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control form-control-lg border-2 text-white"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        style={{ 
                          borderRadius: '12px',
                          backgroundColor: '#0f1419',
                          borderColor: '#2d3748',
                          color: '#ffffff'
                        }}
                      >
                        <option value="admin" style={{ backgroundColor: '#0f1419', color: '#ffffff' }}>Admin - Full system access</option>
                        <option value="production_manager" style={{ backgroundColor: '#0f1419', color: '#ffffff' }}>Production Manager - Manage production</option>
                        <option value="production_associate" style={{ backgroundColor: '#0f1419', color: '#ffffff' }}>Production Associate - Production tasks</option>
                        <option value="inhouse_installer" style={{ backgroundColor: '#0f1419', color: '#ffffff' }}>Inhouse Installer - Installation tasks</option>
                      </select>
                      <small className="text-light opacity-75">Select appropriate role based on user responsibilities</small>
                    </div>
                  </div>
                </div>

                {/* Role Information Card */}
                <div className="row mt-4">
                  <div className="col-12">
                    <div className="alert border-0" style={{ 
                      backgroundColor: '#1e293b', 
                      borderLeft: '4px solid #6366f1',
                      borderRadius: '12px'
                    }}>
                      <div className="d-flex">
                        <i className="fas fa-info-circle me-3 mt-1" style={{ color: '#6366f1' }}></i>
                        <div>
                          <h6 className="mb-2 text-white">Role Permissions</h6>
                          <ul className="mb-0 small text-light opacity-75">
                            <li><strong className="text-white">Admin:</strong> Complete system access and user management</li>
                            <li><strong className="text-white">Production Manager:</strong> Oversee production processes and team management</li>
                            <li><strong className="text-white">Production Associate:</strong> Execute production tasks and report to managers</li>
                            <li><strong className="text-white">Inhouse Installer:</strong> Handle installation and maintenance tasks</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Footer */}
              <div className="card-footer border-top py-4" style={{ 
                backgroundColor: '#232946', 
                borderTop: '1px solid #2d3748',
                borderRadius: '0 0 16px 16px'
              }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-light opacity-75 small">
                    <i className="fas fa-shield-alt me-2" style={{ color: '#6366f1' }}></i>
                    All user data is encrypted and secure
                  </div>
                  <div className="d-flex gap-2">
                    <button 
                      type="button" 
                      className="btn btn-outline-light px-4"
                      onClick={() => navigate('/user/manage-user')}
                      style={{ borderColor: '#4a5568', color: '#a0aec0' }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn px-4"
                      disabled={userLoading}
                      style={{ 
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        border: 'none',
                        color: 'white'
                      }}
                    >
                      {userLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus me-2"></i>
                          Create User
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* MUI Snackbar for alerts */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={handleAlertClose}
      >
        <Alert onClose={handleAlertClose} severity={alertType}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CreateNewUser;